import React, { useMemo, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FolderOpen, Target, Phone, ClipboardCheck } from 'lucide-react';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  Sun, 
  Wrench, 
  Sparkles,
  TrendingUp,
  FileUp,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Download,
  Shield,
  Map,
  ClipboardList,
  FileText,
  Euro,
  Globe,
  Zap,
  Package,
  Warehouse,
  Mail,
  Ticket,
  BarChart3,
  Clock
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import api from '../../lib/api';

// Navigation items with translation keys
const crmNavItems = [
  { icon: LayoutDashboard, labelKey: 'dashboard', label: 'Dashboard', path: '/dashboard', roles: ['admin', 'technician', 'sales', 'cleaner'] },
  { icon: Ticket, labelKey: 'tickets', label: 'Tickets', path: '/tickets', roles: ['admin', 'technician'] },
  { icon: Phone, label: 'AI Telefoon', path: '/ai-telefoon', roles: ['admin'] },
  { icon: Mail, labelKey: 'mails', label: 'Mails', path: '/mails', roles: ['admin', 'sales'], hasBadge: true },
  { icon: ClipboardList, labelKey: 'requests', label: 'Aanvragen', path: '/aanvragen', roles: ['admin', 'sales'] },
  { icon: TrendingUp, labelKey: 'salesOverview', label: 'Sales', path: '/sales', roles: ['admin'] },
  { icon: Calendar, labelKey: 'agenda', label: 'Agenda', cleanerLabelKey: 'cleaning', cleanerLabel: 'Reinigingen', path: '/agenda', roles: ['admin', 'technician', 'sales', 'cleaner'] },
  { icon: Users, labelKey: 'customers', label: 'Klanten', path: '/customers', roles: ['admin', 'technician'] },
  { icon: Zap, labelKey: 'installations', label: 'Installaties', path: '/installaties', roles: ['admin', 'technician', 'cleaner'] },
  { icon: Sparkles, labelKey: 'cleaning', label: 'Reinigingen', path: '/cleaning', roles: ['admin', 'technician'] },
  { icon: FileText, labelKey: 'quotes', label: 'Offertes', path: '/offertes', roles: ['admin', 'sales'] },
  { icon: Euro, labelKey: 'invoices', label: 'Facturen', path: '/facturen', roles: ['admin'] },
  { icon: Target, label: 'Leads', path: '/leads', roles: ['admin'] },
  { icon: BarChart3, label: 'Financieel', path: '/financieel', roles: ['admin'] },
  { icon: FolderOpen, label: 'Sharepoint', path: '/sharepoint', roles: ['admin', 'technician', 'sales'] },
  { icon: Zap, label: "Schema's", path: '/schemas', roles: ['admin'] },
  { icon: ClipboardCheck, label: 'Keuringen', path: '/keuringen', roles: ['admin'] },
  { icon: Clock, label: 'Time Sheets', path: '/timesheets', roles: ['cleaner', 'technician'] },
  { icon: Clock, label: 'Time Sheets Team', path: '/timesheets-team', roles: ['admin'] },
  { icon: Package, labelKey: 'products', label: 'Producten', path: '/producten', roles: ['admin'] },
  { icon: Warehouse, labelKey: 'stock', label: 'Voorraad', path: '/stock', roles: ['admin', 'technician'] },
  { icon: FileUp, label: 'CSV Import', path: '/import', roles: ['admin'] },
  { icon: Download, labelKey: 'export', label: 'Export', path: '/export', roles: ['admin'] },
  { icon: Settings, labelKey: 'settings', label: 'Instellingen', path: '/settings', roles: ['admin', 'sales', 'cleaner'] },
  { icon: Shield, labelKey: 'admin', label: 'Admin', path: '/admin', roles: ['admin'] },
];

// Platform-only navigation (for super_admin)
const platformNavItems = [
  { icon: Globe, labelKey: 'platformAdmin', label: 'Platform Beheer', path: '/platform', roles: ['admin'] },
];

export const Sidebar = ({ collapsed, setCollapsed, isMobile, mobileMenuOpen }) => {
  const { user, logout, hasRole, exitCompanyCRM, viewingCompany, isModuleEnabled, getModuleName, companyConfig } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Email queue count for badge
  const [emailQueueCount, setEmailQueueCount] = useState(0);
  
  // Fetch email queue count periodically
  useEffect(() => {
    const fetchQueueCount = async () => {
      try {
        const res = await api.get('/email-queue/count');
        setEmailQueueCount(res.data.queued || 0);
      } catch (error) {
        // Silently fail - badge just won't show
      }
    };
    
    // Only fetch if user is logged in and has company
    if (user?.company_id || user?.viewingCompanyId) {
      fetchQueueCount();
      // Refresh every 60 seconds
      const interval = setInterval(fetchQueueCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.company_id, user?.viewingCompanyId]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToPlatform = () => {
    exitCompanyCRM();
    navigate('/platform');
  };

  // Super Admin (without active company) sees only Platform items
  // Regular users or Super Admin viewing a company sees CRM items
  const isSuperAdminOnly = user?.platform_role === 'super_admin' && !user?.viewingCompanyId;
  
  // Map nav items to their module keys
  const moduleKeyMap = {
    '/agenda': 'agenda',
    '/cleaning': 'reinigingen',
    '/installaties': 'installaties',
    '/offertes': 'offertes',
    '/facturen': 'facturen',
    '/stock': 'stock',
    // Don't map /producten to any module - always show if role allows
  };
  
  // Map module keys to display name replacements for nav items
  const moduleToNavMap = {
    'agenda': ['/agenda'],
    'reinigingen': ['/cleaning'],
    'installaties': ['/installaties'],
    'offertes': ['/offertes'],
    'facturen': ['/facturen'],
    'stock': ['/stock'],
  };
  
  const navItems = isSuperAdminOnly ? platformNavItems : crmNavItems;
  
  // Filter nav items by role AND by module enabled status
  // Use useMemo to recalculate when companyConfig changes
  const filteredNavItems = useMemo(() => {
    const items = navItems.filter(item => {
      // First check role
      if (!hasRole(item.roles)) return false;
      
      // Then check if module is enabled (if applicable)
      const moduleKey = moduleKeyMap[item.path];
      if (moduleKey && !isModuleEnabled(moduleKey)) {
        return false;
      }
      
      return true;
    }).map(item => {
      // Apply custom module names if configured
      const moduleKey = moduleKeyMap[item.path];
      if (moduleKey && companyConfig?.modules_config?.[moduleKey]) {
        const moduleConfig = companyConfig.modules_config[moduleKey];
        if (moduleConfig.display_name && moduleConfig.display_name !== item.label) {
          return {
            ...item,
            label: moduleConfig.display_name
          };
        }
      }
      return item;
    });
    
    // Deduplicate items with same label (e.g., both Producten and Voorraad → Materialen)
    // Keep only the first occurrence based on path order
    const seenLabels = new Set();
    return items.filter(item => {
      if (seenLabels.has(item.label)) {
        return false;
      }
      seenLabels.add(item.label);
      return true;
    });
  }, [navItems, companyConfig, hasRole, isModuleEnabled]);

  // On mobile, hide completely when not open
  if (isMobile && !mobileMenuOpen) {
    return null;
  }

  // Platform branding - use company logo/name from config if available
  const platformName = isSuperAdminOnly 
    ? "Business Assistant" 
    : (companyConfig?.branding?.company_name || user?.companyName || user?.company_name || "Business Assistant");
  
  // Get logo URL from branding
  const logoUrl = companyConfig?.branding?.logo_url;

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-full bg-card border-r border-border z-40 flex flex-col transition-all duration-300",
        isMobile ? "w-64" : (collapsed ? "w-16" : "w-64")
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        {!collapsed && (
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-8 h-8 rounded-md flex items-center justify-center",
                isSuperAdminOnly ? "bg-gradient-to-br from-amber-500 to-orange-600" : "bg-primary"
              )}>
                {isSuperAdminOnly ? (
                  <Zap className="w-5 h-5 text-white" strokeWidth={2} />
                ) : (
                  <Sun className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
                )}
              </div>
              <span className="font-semibold text-sm tracking-tight">{platformName}</span>
            </div>
            {isSuperAdminOnly && (
              <span className="text-[10px] text-muted-foreground mt-0.5 ml-10">AI-powered CRM voor uw bedrijf</span>
            )}
          </div>
        )}
        {collapsed && (
          <div className={cn(
            "w-8 h-8 rounded-md flex items-center justify-center mx-auto",
            isSuperAdminOnly ? "bg-gradient-to-br from-amber-500 to-orange-600" : "bg-primary"
          )}>
            {isSuperAdminOnly ? (
              <Zap className="w-5 h-5 text-white" strokeWidth={2} />
            ) : (
              <Sun className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
            )}
          </div>
        )}
      </div>

      {/* Back to Platform Banner - shown when Super Admin is viewing a company */}
      {viewingCompany && user?.platform_role === 'super_admin' && !collapsed && (
        <div className="px-2 py-2 border-b border-border">
          <button
            onClick={handleBackToPlatform}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-gradient-to-r from-amber-500/10 to-orange-600/10 hover:from-amber-500/20 hover:to-orange-600/20 text-amber-600 transition-colors"
            data-testid="back-to-platform"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Terug naar Platform</span>
          </button>
          <p className="text-xs text-muted-foreground mt-1 px-3 truncate">
            Bekijkt: {viewingCompany.name}
          </p>
        </div>
      )}
      {viewingCompany && user?.platform_role === 'super_admin' && collapsed && (
        <div className="px-2 py-2 border-b border-border flex justify-center">
          <button
            onClick={handleBackToPlatform}
            className="w-8 h-8 rounded-md flex items-center justify-center bg-gradient-to-r from-amber-500/10 to-orange-600/10 hover:from-amber-500/20 hover:to-orange-600/20 text-amber-600 transition-colors"
            title="Terug naar Platform"
            data-testid="back-to-platform-collapsed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {filteredNavItems.map((item) => {
            // Priority: custom label from config > translated label > default label
            let displayLabel;
            
            // Check if this item has a custom label from company config
            const moduleKey = moduleKeyMap[item.path];
            const hasCustomLabel = moduleKey && companyConfig?.modules_config?.[moduleKey]?.display_name;
            
            if (hasCustomLabel) {
              // Use the custom label from company config
              displayLabel = companyConfig.modules_config[moduleKey].display_name;
            } else if (user?.role === 'cleaner' && item.cleanerLabelKey) {
              displayLabel = t(item.cleanerLabelKey);
            } else if (item.labelKey) {
              displayLabel = t(item.labelKey);
            } else {
              displayLabel = item.label;
            }
            
            return (
              <li key={item.path} className="relative">
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                      isActive 
                        ? "bg-primary/10 text-primary" 
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                      collapsed && "justify-center px-2"
                    )
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && (
                    <span className="flex items-center gap-2">
                      {displayLabel}
                      {item.hasBadge && emailQueueCount > 0 && (
                        <Badge variant="destructive" className="h-5 min-w-5 px-1.5 flex items-center justify-center text-xs">
                          {emailQueueCount}
                        </Badge>
                      )}
                    </span>
                  )}
                </NavLink>
                {collapsed && item.hasBadge && emailQueueCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom section */}
      <div className="p-2 border-t border-border">
        <Separator className="my-2" />

        {/* User info */}
        {!collapsed && user && (
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium truncate">{user.first_name} {user.last_name}</p>
            <p className="text-xs text-muted-foreground capitalize">{user.role === 'cleaner' ? 'Install/Clean' : user.role}</p>
          </div>
        )}

        <Button
          variant="ghost"
          className={cn(
            "w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10",
            collapsed ? "px-2 justify-center" : "justify-start"
          )}
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          {!collapsed && <span className="ml-3">{t('logout')}</span>}
        </Button>

        {/* Collapse toggle */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-2 text-muted-foreground"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <ChevronLeft className="w-4 h-4 mr-2" />
              <span>{t('collapse')}</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
};
